import React, { useEffect, useState } from 'react';
import BadgesComponent from '@components/common/Badges';
import './PrivielegeDetails.css';
import CommonCheckboxTable from '@components/common/CommonCheckboxTable';
import { useSelector } from 'react-redux';
import { RootState } from '@store/reducers';
import { getData } from '../../services/CommonApiService';
import endpoints from '@config/endpoints';
import { useDispatch } from 'react-redux';
import { updateForm } from '@store/actions/userManagementAction';
import SearchableDropdownComponent from '@components/common/SearchableDropDown';
import Auth from "../../utils/auth";
import InfoBar from '@components/common/InfoBars';
import { errorLogger } from '@utils/applicationLogUtils';

interface overviewProps {
  flow: string;
  page: string;
  roleDetails?: any;
}

type PrivilegePageType = {
  privileges: any[]; 
  page_name: any; 
  page_id: any;
}

interface formDataType {
  [key: string]: any;
}

const comomnColumnHeader = [
  {
    name: 'tableName',
    key: 'title',
    sortable: true,
    filterRequired: true,
    filterDefault: false,
    width: 120,
  },
  {
    name: 'Privileges',
    key: 'privileges',
    sortable: false,
    filterRequired: true,
    filterDefault: true,
    width: 815,
  },
];


const PrivilegeDetails = (props: overviewProps) => {
const roleNameDataObj = {
  role_name: {
    '#type': 'select',
    '#title': 'Role Name',
    '#webform_key': 'role_name',
    '#required': true,
    '#options': [],
  },
};
  const { flow, page, roleDetails } = props;
  const [commonPrivilegeData, setCommonPrivilegeData] = useState<any[]>([]);
  const isEditable = useSelector((state: RootState) => state.user.edit_privilege_flow);
  const isPrivilegeSelected = useSelector((state: RootState) => state.user.isPrivilegeSelected);
  const compositeRoleId = roleDetails?.composite_role_id;
  const dispatch = useDispatch();
  const [roleNameData, setRoleNameData] = useState<formDataType>(roleNameDataObj);

  const [roleNameValue, setRoleNameValue] = useState({
    isError: false,
    message: '',
    isDisable: false,
    value: '',
    label: '',
    id: undefined
  });

  const formatPrivilegeData = (privilegeList: any) => {
    // Seperating the data by module and page.
    const groupedByModulesAndPages = privilegeList.data.reduce((acc:any, item:any) => {
        const moduleIndex = acc.findIndex((m: {module_id: any}) => m.module_id === item.module_id);
        
        if (moduleIndex === -1) {
          // New module, add it
          acc.push({
            module_id: item.module_id,
            module_name: item.module_name,
            module_code: item.module_code,
            pages: [{
              page_id: item.page_id,
              page_name: item.page_name,
              privileges: [{
                privilege_id: item.privilege_id,
                privilege_name: item.privilege_name,
                opt_privilege: item.opt_privilege
              }]
            }]
          });
        } else {
          // Existing module, find the page
          const pageIndex = acc[moduleIndex].pages.findIndex((p: { page_id: any; }) => p.page_id === item.page_id);
          


          if (pageIndex === -1) {
            // New page in the module
            acc[moduleIndex].pages.push({
              page_id: item.page_id,
              page_name: item.page_name,
              privileges: [{
                privilege_id: item.privilege_id,
                privilege_name: item.privilege_name,
                opt_privilege: item.opt_privilege
              }]
            });
          } else {
            // Existing page, add the privilege
            acc[moduleIndex].pages[pageIndex].privileges.push({
              privilege_id: item.privilege_id,
              privilege_name: item.privilege_name,
              opt_privilege: item.opt_privilege
            });
          }
        }
        return acc;
      }, []);
      
      // Grouping on the basis of header and refactoring it how it accepts.
      const moduleLevelPrivilegeData = groupedByModulesAndPages.reduce((acc: any, module: any) => {
        const [module_name, module_subName] = module.module_name.split(' - ');
        const [category, subCategory] = module.module_code.split('_');
      
        const privileges = module.pages.flatMap((page: PrivilegePageType) => 
          page.privileges.map(privilege => ({
            name: privilege.privilege_name.charAt(0).toUpperCase() + privilege.privilege_name.slice(1) + " - " + page.page_name,
            enabled: privilege.opt_privilege,
            page_id: page.page_id,
            page_name: page.page_name,
            privilege_id: privilege.privilege_id
          }))
        );

        // Check if the first name is "Common Master"
        const key = module_name === "Common Master" ? (module_subName ? module_subName : module_name) : module_name;

        if (!acc[category]) {
          acc[category] = [];
        }
        
        acc[category].push({
          title: module_subName ? module_subName : module.module_name,
          module_name: key,
          module_code: category,
          privileges: privileges
        });

        return acc;

      }, {});

      

      const tempData: any[] = [];
      Object.keys(moduleLevelPrivilegeData).map((key) => tempData.push(moduleLevelPrivilegeData[key]));

      const combinedPayloadData = combineTransformedData(tempData);
      dispatch(updateForm("role_payload", combinedPayloadData));

      setCommonPrivilegeData(moduleLevelPrivilegeData);
  }

  const transformDataToPayload = (data: any[]) => {
    const result: any = {};
  
    data.forEach(module => {
      module.privileges.forEach((privilege: any) => {
        if (privilege.enabled) {
          if (!result[privilege.page_id]) {
            result[privilege.page_id] = [];
          }
          result[privilege.page_id].push(privilege.privilege_id);
        }
      });
    });
  
    return result;
  };

  const combineTransformedData = (dataSets: any[][]) => {
    const combinedResult: { [key: string]: string[] } = {};
  
    dataSets.forEach(dataSet => {
      const transformedData = transformDataToPayload(dataSet);
      Object.keys(transformedData).forEach(pageId => {
        if (!combinedResult[pageId]) {
          combinedResult[pageId] = [];
        }
        combinedResult[pageId] = combinedResult[pageId].concat(transformedData[pageId]);
      });
    });
  
    return combinedResult;
  };

  const getPrivilegesList = async () => {
    try {
      if (flow === 'createNewRole') {
        const privilegeList = await getData<any[]>(
          `${endpoints.getListofPrivilegesAll}`,
        );
        formatPrivilegeData(privilegeList);
      } else if (flow === 'view' || flow === 'edit') {

        const role_name_options = roleDetails?.compositeRoleIds.map((key: any) => ({
          value: key.role_name,
          label: key.role_name,
          id: key.compositeRoleId
        }))

        dispatch(updateForm("compositeRoleId", roleDetails?.compositeRoleIds[0].compositeRoleId));
        setRoleNameValue((prevState: any) => ({
          ...prevState,
          value : roleDetails?.compositeRoleIds[0].role_name,
          label : roleDetails?.compositeRoleIds[0].role_name
        }))
        setRoleNameData(() =>({
          'role_name' : {
            ...roleNameData['role_name'],
            '#options': role_name_options
          }
        }))        
        const privilegeList = await getData<any[]>(
          `${endpoints.getPrivilegeByUserId(roleDetails?.user_id,roleDetails?.compositeRoleIds[0].compositeRoleId)}`
        );
        formatPrivilegeData(privilegeList);
      } else {
        const privilegeList = await getData<any[]>(
          `${endpoints.getPrivilegeList}/${compositeRoleId}`
        );
        
        formatPrivilegeData(privilegeList);
      }
    }     catch(error:any){
      errorLogger(error?.response?.data||error?.response||error)
    }
  }
  
  useEffect(() => {
    getPrivilegesList();
    (flow === 'editRole' || flow === 'createNewRole' || flow === 'edit') && dispatch(updateForm("edit_privilege_flow", true));
    return() => {
      dispatch(updateForm("edit_privilege_flow", false));
    }
  }, []);

  const handleRoleNameChage = async (value: any, key: string, id?: any, option?: any, code?: any, field?: any) => {
    if (value === roleNameValue.value) {
      return;
    }
    try {
      setRoleNameValue((prevState: any) => ({
        ...prevState,
        value: value,
        label: value,
      }));
  
      const privilegeList = await getData<any[]>(
        `${endpoints.getPrivilegeByUserId(roleDetails?.user_id,id)}`,
      );
      formatPrivilegeData(privilegeList);
      dispatch(updateForm('compositeRoleId', id));  
    }     catch(error:any){
      errorLogger(error?.response?.data||error?.response||error)
    }
  }
  
  const handleCheckboxChange = ( changedData: any, moduleName: any, privilegeIndex: any, tableName: any ) => {
    if (!isEditable) {
      return;
    };
    let updatedPrivileges = commonPrivilegeData[tableName].map((element: any) => {

      if(element.title === moduleName) {
        element.privileges[privilegeIndex].enabled = changedData;
        const checkboxName = element.privileges[privilegeIndex].name;
        const [updatedCheckboxName, pagename] = checkboxName.split(' - ');
        const pageName = element.privileges[privilegeIndex].page_name;
    
        element.privileges = element.privileges.map((item: any) => {
          const [existingName, pagename] = item.name.split(' - ');
          if ((checkboxName !==  "View - Overview Page" && checkboxName !==  "View - List Page") && existingName === updatedCheckboxName && item.page_name !== pageName) {
            item.enabled = changedData;
          }
          return item;
        });

        
        if(!changedData && element.privileges[privilegeIndex].name === "View - List Page"){
          element.privileges = element.privileges.map(
            (privilege: any) => {
              return { ...privilege, enabled: false };
            },
          ); 
        }

        if(changedData && element.privileges[privilegeIndex].name !== "View - List Page"){
          element.privileges = element.privileges.map(
            (privilege: any) => {
              return privilege.name === "View - List Page" ? {...privilege, enabled: true} : privilege;
            },
          ); 
        }


      }
      return element;
    })

    updatedPrivileges = { ...commonPrivilegeData, 
      [tableName] : updatedPrivileges
    }
    const tempData: any[] = [];
    Object.keys(updatedPrivileges).map((key) => tempData.push(updatedPrivileges[key]));

    setCommonPrivilegeData(updatedPrivileges);
    const combinedPayloadData = combineTransformedData(tempData);
    dispatch(updateForm('role_payload', combinedPayloadData));
  }

  return (
    <>
      <div className="privilege">
        <div className="overview">
          <BadgesComponent
            className="over-badge"
            badgeName="Role details"
            variant="primary-1"
            badgeType="case5"
          />

          <div className="overview-details">
            <div>
              <label className="overview-details-label">User Type</label>
              {
                <div className="overview-details-value">
                  {roleDetails?.user_type}
                </div>
              }
            </div>
            {!(flow === 'view' || flow === 'edit') ? (
              <div>
                <label className="overview-details-label">Role Name</label>
                <div className="overview-details-value">
                  {roleDetails?.role_name}
                </div>
              </div>
            ) : (
              <>
              <SearchableDropdownComponent
              options={roleNameData.role_name['#options']}
              onChange={(changedData: any) => handleRoleNameChage( changedData?.value, 'key', changedData?.id, 'dataOptions', changedData?.code , changedData)}
              placeHolder={'Role Name'}
              multipleSelection={false}
              dropDownType={'general'}
              dropLabelName={'Role Name'}
              ifRequired={false}
              dropDownDisable={false}
              dropdownsearchable={true}
              value={roleNameValue}
              errorMessage={roleNameValue?.message}
              isClearable={false}
          />
          </>
            )}
            <div>
              <label className="overview-details-label">User Designation</label>
              <div className="overview-details-value">
                {roleDetails?.user_designation}
              </div>
            </div>

            {!(flow === 'view' || flow === 'edit') && <>
            <div>
              <label className="overview-details-label">Status</label>
              <div className="overview-details-value">
                {roleDetails?.status}
              </div>
            </div>
            <div>
              <label className="overview-details-label">Description</label>
              <div className="overview-details-value">
                {roleDetails?.description}
              </div>
            </div>
            </>}

          </div>
        </div>
        {(flow === 'createNewRole' && !isPrivilegeSelected) && <div className="privilege-infobar">
        <InfoBar
            variant="danger"
            message={"Please assign privileges"}
            className="input_field_class"
          />
        </div>}
        <div className="table-content">
          <div className="user-mgmt-content">
            <div className="user-table">
              {Object.keys(commonPrivilegeData).length > 0 &&
                Object.keys(commonPrivilegeData).map((key: any) => (
                  <>
                  <BadgesComponent
                  className="over-badge"
                  badgeName={commonPrivilegeData[key][0].module_name}
                  variant="primary-1"
                  badgeType="case5"
                />
                <CommonCheckboxTable
                    handleCheckboxChange={handleCheckboxChange}
                    key={key}
                    data={commonPrivilegeData[key]}
                    columnHeader={[
                      {
                        ...comomnColumnHeader[0],
                        name: commonPrivilegeData[key][0].module_name,
                      },
                      ...comomnColumnHeader.slice(1),
                    ]}
                    gridFor={'role'}
                  />
                </> 
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivilegeDetails;